import { type FieldValues, get } from "react-hook-form";
import type { GridValidRowModel } from "@mui/x-data-grid-premium";
import type { ValidFormField } from "src/components/Form/Form.types.ts";
import { camelToSnakeCase } from "@exonn/utils";
import { logger } from "src/logger";
const warnedAboutOldApproachOfLoadingFallbackOptions = new Set();

const warnKey = (resource: string, name: string) => {
  return `${resource}-${name}`;
};

export const getAdditionalFormValues = <
  FormValues extends FieldValues = FieldValues,
>({
  mainResource,
  resource,
  action,
  processedFields,
}: {
  mainResource?: GridValidRowModel;
  resource: string;
  action: "edit" | "create";
  processedFields: Record<keyof FormValues, ValidFormField<FormValues>>;
}) => {
  if (action === "create") {
    return {};
  }
  if (!mainResource) {
    return {};
  }
  return Object.keys(processedFields).reduce((acc, name) => {
    const field = processedFields[name];
    if (field.type === "select" && field.serverOptions) {
      if (!field.getInitialOptionsFromMainResource) {
        if (
          !warnedAboutOldApproachOfLoadingFallbackOptions.has(
            warnKey(resource, name),
          )
        ) {
          warnedAboutOldApproachOfLoadingFallbackOptions.add(
            warnKey(resource, name),
          );
          logger.debug(
            `prefer loading fallback options together with request that loads main resource for select [${name}] in ${resource} form instead of separate request`,
          );
        }
        return acc;
      }

      const defaultGetter = (mainResource: any) => {
        const camelCasedKey = camelToSnakeCase(name);
        const resolvedKey = field.resourceKey ?? camelCasedKey;
        return get(mainResource, resolvedKey);
      };

      const getter = field.getResourcesForInitialOptions ?? defaultGetter;
      const foundResourceOrResources = getter(mainResource);

      if (foundResourceOrResources) {
        // todo: check includes.
        // includes that are specified in select server options must match includes that are specified in main form config
        return {
          [`${name}_resources`]: field.isMultiple
            ? foundResourceOrResources
            : [foundResourceOrResources],
          ...acc,
        };
      }
      logger.error(
        `[${resource} form] [${name} select]: resources for fallback options are not found in main resource. check form includes`,
        {
          mainResource,
          field,
        },
      );
    }
    return acc;
  }, {});
};
